import { useLayoutEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@shared/ui/skeleton";
import {
  FullBleedPendingSkeleton,
  isFullBleedPending,
} from "./route-pending-full-bleed";

/**
 * Route-pending placeholder: a content-shaped skeleton (page title, a lead
 * paragraph, a card grid) instead of a centred spinner, so the transition keeps
 * the layout the page is about to fill. The accessible "Loading…" text is
 * screen-reader-only; the pulse animation is disabled for reduced motion.
 */
export function RoutePendingFallback() {
  const { t } = useTranslation("common");
  const loadingLabel = t("loading", { defaultValue: "Loading..." });

  // Full-bleed routes (Health Assistant, Messages) get a sidebar + chat shaped
  // skeleton of the same fixed height as the page they are about to show.
  if (isFullBleedPending()) {
    return <FullBleedPendingSkeleton loadingLabel={loadingLabel} />;
  }

  return (
    <div
      className="mx-auto w-full max-w-5xl space-y-6 bg-background px-4 py-6 lg:px-6"
      data-testid="route-pending-fallback"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">{loadingLabel}</span>
      <div aria-hidden="true" className="space-y-6 motion-reduce:[&_*]:animate-none">
        <div className="space-y-3">
          <Skeleton className="h-8 w-1/3 max-w-xs" />
          <Skeleton className="h-4 w-2/3 max-w-lg" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32 sm:col-span-2 lg:col-span-1" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  );
}

/**
 * `defaultPendingMs: 200` — a route that resolves inside 200 ms never shows
 * the skeleton at all (no flash). Once it does show, `defaultPendingMinMs`
 * keeps it up for at least 250 ms so it does not blink either.
 */
export const ROUTER_PENDING_OPTIONS = {
  defaultPendingComponent: RoutePendingFallback,
  defaultPendingMs: 200,
  defaultPendingMinMs: 250,
} as const;

export const ROUTE_FADE_MS = 150;

/**
 * Short fade-in for freshly mounted route content. `App` keys its Suspense
 * boundary by pathname, so this remounts (and re-fades) on every navigation.
 *
 * Uses the Web Animations API with an opacity-only keyframe pair on purpose:
 * the tailwindcss-animate `animate-in` keyframes also write `transform`, and
 * even an identity transform makes the wrapper the containing block for
 * `position: fixed` descendants (the mobile bottom nav) for the duration.
 * Skipped entirely under `prefers-reduced-motion: reduce`.
 */
export function RouteFade({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof el.animate !== "function") return undefined;
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }
    const animation = el.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: ROUTE_FADE_MS,
      easing: "ease-out",
    });
    return () => animation.cancel();
  }, []);

  return (
    <div ref={ref} data-testid="route-transition" className="motion-reduce:animate-none">
      {children}
    </div>
  );
}
